/*
 * ====================================================================================================
 * WARNING: BACKEND-COPIED DTO FILE
 * THIS FILE IS COPIED FROM THE BACKEND.
 * DO NOT MODIFY THIS FILE MANUALLY IN FRONTEND CODE.
 * IT MAY BE DELETED, REGENERATED, OR REPLACED AT ANY TIME.
 * ====================================================================================================
 */

/**
 * Resolved bike station linked to a rental leg for map popup and availability.
 */
export class BikeStationInfoDTO {

    /**
     * Bike station identifier as provided by the operator feed.
     * Example: 22289
     */
    stationId!: string;

    /**
     * Bike sharing operator (GBFS network identifier) used by the station status endpoint.
     * Example: publibike
     */
    operator!: string;

    /**
     * Bike station display name.
     * Example: Lausanne-Flon
     */
    name!: string;

    /**
     * Bike station latitude in WGS84 decimal degrees.
     * Example: 46.516
     */
    lat!: number;

    /**
     * Bike station longitude in WGS84 decimal degrees.
     * Example: 6.629
     */
    lon!: number;

}

/**
 * Rental vehicle form factors supported in itinerary and offer payloads.
 */
export enum RentalVehicleType {
    "BICYCLE" = "BICYCLE",
    "CARGO_BICYCLE" = "CARGO_BICYCLE",
    "CAR" = "CAR",
    "MOPED" = "MOPED",
    "SCOOTER_STANDING" = "SCOOTER_STANDING",
    "SCOOTER_SEATED" = "SCOOTER_SEATED",
    "OTHER" = "OTHER",
}

/**
 * Rental vehicle propulsion types supported in itinerary and offer payloads.
 */
export enum RentalVehiclePropulsion {
    "HUMAN" = "HUMAN",
    "ELECTRIC_ASSIST" = "ELECTRIC_ASSIST",
    "ELECTRIC" = "ELECTRIC",
    "COMBUSTION" = "COMBUSTION",
    "COMBUSTION_DIESEL" = "COMBUSTION_DIESEL",
    "HYBRID" = "HYBRID",
    "PLUG_IN_HYBRID" = "PLUG_IN_HYBRID",
    "HYDROGEN_FUEL_CELL" = "HYDROGEN_FUEL_CELL",
}

/**
 * Rental return constraint types supported in itinerary and offer payloads.
 */
export enum RentalReturnConstraint {
    "NONE" = "NONE",
    "ORIGIN_STATION" = "ORIGIN_STATION",
    "ANY_STATION" = "ANY_STATION",
}

/**
 * Shared rental metadata shape reused by itinerary legs and rental offers.
 */
export class RentalLegPropertiesDTO {

    /**
     * Vehicle metadata for the rental leg.
     * Example: {
     *             "type":       RentalVehicleType.BICYCLE,
     *             "brand":      "PubliBike",
     *             "model":      "Classic",
     *             "propulsion": RentalVehiclePropulsion.HUMAN,
     *         }
     * Type: object
     */
    vehicle?: {
        readonly "type":        RentalVehicleType;
        readonly "brand"?:      string;
        readonly "model"?:      string;
        readonly "propulsion"?: RentalVehiclePropulsion;
    };

    /**
     * Rental provider metadata.
     * Example: {
     *             "id":    "publibike",
     *             "name":  "PubliBike",
     *             "url":   "https://www.publibike.ch",
     *             "color": "#f5c518",
     *         }
     * Type: object
     */
    provider?: {
        readonly "id":     string;
        readonly "name":   string;
        readonly "url"?:   string;
        readonly "color"?: string;
    };

    /**
     * Deep-link URIs exposed by the rental provider.
     * Example: {
     *             "default": "https://www.publibike.ch",
     *             "iOS":     "publibike://open",
     *             "android": "intent://publibike/open",
     *         }
     * Type: object
     */
    uris?: {
        readonly "default"?: string;
        readonly "iOS"?:     string;
        readonly "android"?: string;
    };

    /**
     * Rental station references for pickup and return.
     * Example: {
     *             "from": "station-001",
     *             "to":   "station-042",
     *         }
     * Type: object
     */
    stations?: {
        readonly "from": string;
        readonly "to":   string;
    };

    /**
     * Rental return policy when provided.
     * Example: RentalReturnConstraint.ANY_STATION
     * Enum: RentalReturnConstraint
     */
    returnConstraint?: RentalReturnConstraint;

    /**
     * Matched bike station (pickup) when the rental is a bicycle and a station was found nearby.
     * Type: BikeStationInfoDTO
     */
    bikeStation?: BikeStationInfoDTO;

}
